import { graphqlClient } from '@/lib/graphql-client';
import { EmployeeOption, getEmployeesByUserIds } from '@/modules/shared/hr-lookups';
import {
  getCompletedPmReviewDetailsWithForms,
  getPmReviews,
  PmReview,
} from '@/modules/performance/hr-review-core';
import {
  buildGetLatestKpiConfigurationQuery,
  buildGetQuantitativeDataQuery,
} from '../graphql/queries';
import { PerformanceDashboard } from '../types/dashboard.types';
import { buildPerformanceDashboard } from '../utils/build-dashboard';
import { DEFAULT_SCORING_CONFIG, QuantitativeRow, ScoringConfig } from '../utils/scoring-engine';

interface KpiConfigurationRow {
  CreatedDate?: string;
  QnMatrixMinLevel?: number;
  QnMatrixMaxLevel?: number;
  NormalizedMaxScale?: number;
  ReviewerGroupWeightsJson?: string;
  CategoryWeightsJson?: string;
}

const parseWeights = (json: string | undefined, fallback: Record<string, number>) => {
  if (!json) return fallback;
  try {
    const parsed = JSON.parse(json) as Record<string, number>;
    return Object.keys(parsed).length ? parsed : fallback;
  } catch {
    return fallback;
  }
};

export const toScoringConfig = (row?: KpiConfigurationRow | null): ScoringConfig => {
  if (!row) return DEFAULT_SCORING_CONFIG;
  return {
    qnMatrixMinLevel: row.QnMatrixMinLevel ?? DEFAULT_SCORING_CONFIG.qnMatrixMinLevel,
    qnMatrixMaxLevel: row.QnMatrixMaxLevel ?? DEFAULT_SCORING_CONFIG.qnMatrixMaxLevel,
    normalizedMaxScale: row.NormalizedMaxScale || DEFAULT_SCORING_CONFIG.normalizedMaxScale,
    reviewerGroupWeights: parseWeights(
      row.ReviewerGroupWeightsJson,
      DEFAULT_SCORING_CONFIG.reviewerGroupWeights
    ),
    categoryWeights: parseWeights(row.CategoryWeightsJson, DEFAULT_SCORING_CONFIG.categoryWeights),
  };
};

/** The newest configuration row: the query sorts by `CreatedDate` descending on the server. */
const getScoringConfig = async (): Promise<ScoringConfig> => {
  const result = await graphqlClient.query<{ KpiConfigurations: { items: KpiConfigurationRow[] } }>(
    {
      query: buildGetLatestKpiConfigurationQuery({}, 1, 1),
    }
  );
  return toScoringConfig(result.KpiConfigurations.items[0]);
};

const getQuantitativeRows = async (pmReviewId: string): Promise<QuantitativeRow[]> => {
  const result = await graphqlClient.query<{ QuantitativeDatas: { items: QuantitativeRow[] } }>({
    query: buildGetQuantitativeDataQuery({ PmReviewId: pmReviewId }, 1, 200),
  });
  return result.QuantitativeDatas.items;
};

/** Legacy GetLatestPmReviewId: the employee's most recently updated annual (arf) review cycle. `getPmReviews` sorts
 * by `LastUpdatedDate` descending on the server, so the first row is the latest one. */
const getLatestAnnualReview = async (userId: string): Promise<PmReview | null> => {
  const page = await getPmReviews({
    pageNo: 1,
    pageSize: 1,
    filter: { ReviewForId: userId, AssessmentType: 'arf' },
  });
  return page.items[0] ?? null;
};

/** Builds the dashboard of one employee from their latest annual review; null when they have none yet. */
export const getPerformanceDashboard = async (
  userId: string
): Promise<PerformanceDashboard | null> => {
  const review = await getLatestAnnualReview(userId);
  if (!review) return null;

  const [config, completedDetails, quantitativeRows, [employee]] = await Promise.all([
    getScoringConfig(),
    getCompletedPmReviewDetailsWithForms(review.ItemId),
    getQuantitativeRows(review.ItemId),
    getEmployeesByUserIds([userId]),
  ]);

  let manager: EmployeeOption | undefined;
  if (employee?.ReportingManagerUserId) {
    [manager] = await getEmployeesByUserIds([employee.ReportingManagerUserId]);
  }

  return buildPerformanceDashboard({
    review,
    employee,
    managerName: manager?.DisplayName,
    completedDetails,
    quantitativeRows,
    config,
  });
};
